"use client";

import { useState, useEffect, useRef } from "react";
import { isHeicUrl, convertHeicUrlToJpeg, getPendingHeicConversion } from "@/lib/image-url-helper";
import { resolveStorageUrl, type ImageRefInput } from "@/lib/storage/public-url";

/**
 * Hook to handle HEIC image conversion automatically
 * Returns the converted JPEG blob URL if HEIC, or original URL otherwise
 *
 * Second job (relative-storage-path migration): this hook is the READ FUNNEL
 * for every full-resolution gallery / cart / chat image — the callers render
 * `displaySrc || src`, so resolving `rawSrc` once here makes the whole cluster
 * relative-path-safe without touching each render point. It also fixes the
 * `fetch()` inside the conversion, which cannot download a bucket-relative
 * path. `resolveStorageUrl` is idempotent and passes absolute / `blob:` /
 * `data:` / `/local.svg` through unchanged.
 *
 * NOTE the returned `displaySrc` is ALWAYS resolved, so a caller's
 * `displaySrc || src` fallback can only fire when `src` itself is empty.
 *
 * @see lib/storage/url.ts
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
 */
export function useHeicImage(
  rawSrc: ImageRefInput,
  options?: { enabled?: boolean },
): {
  displaySrc: string;
  isConverting: boolean;
  hasError: boolean;
} {
  const src = resolveStorageUrl(rawSrc);
  const [displaySrc, setDisplaySrc] = useState<string>(src);
  const [isConverting, setIsConverting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const isHeic = isHeicUrl(src);
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    // Reset state when src changes
    setHasError(false);
    
    if (!enabled) {
      setIsConverting(false);
      setDisplaySrc(src);
      return;
    }

    if (isHeic && typeof window !== 'undefined' && src) {
      const cachedConversion = getPendingHeicConversion(src);
      if (!cachedConversion) {
        setIsConverting(true);
      }
      const conversion = cachedConversion ?? convertHeicUrlToJpeg(src);
      conversion
        .then((blobUrl) => {
          blobUrlRef.current = blobUrl;
          setDisplaySrc(blobUrl);
          setIsConverting(false);
        })
        .catch((error) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isFormatNotSupported = errorMessage.includes('HEIC_FORMAT_NOT_SUPPORTED');
          const isTimeout = errorMessage.includes('timeout');
          
          // Only log if it's not a timeout (timeouts are expected for large files)
          // Silent failure for timeout - user doesn't need to see this
          if (!isTimeout && !isFormatNotSupported) {
            // Only log unexpected errors (not timeouts or unsupported formats)
            // Errors are handled gracefully by showing fallback image
          }
          
          setHasError(true);
          // Fallback to original URL - browser may show broken image
          // Note: Most browsers won't display HEIC, so it will show broken image icon
          setDisplaySrc(src);
          setIsConverting(false);
        });
    } else {
      setDisplaySrc(src);
    }
    
    return () => {
      blobUrlRef.current = null;
    };
  }, [src, isHeic, enabled]);

  return { displaySrc, isConverting, hasError };
}

